import React, { useEffect, useMemo, useState } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, FormFeedback, Row, Col, UncontrolledTooltip } from 'reactstrap'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { gql, useMutation, useQuery } from '@apollo/client'
import { SubrogationClaimSchema } from './IncidentModalsSchema'
import AddAdjusterModal from './AddAdjusterModal'
import Select from 'react-select'

const CREATE_SUBROGATION_CLAIM = gql`
  mutation CreateSubrogationClaim($input: CreateSubrogationClaimMutationInput!) {
    createSubrogationClaim(input: $input) {
      ok
      errors { field messages }
    }
  }
`

const SUBROGATION_STATUS_CHOICES = gql`
  query SubrogationStatusChoices {
    subrogationStatusChoices: __type(name: "IncidentsSubrogationClaimStatusChoices") {
      enumValues { name description }
    }
  }
`

const ALL_TPAS = gql` query AllTpas($incidentId: ID) {
    allTpas(incidentId: $incidentId, claimType: "SUBROGATION") { 
        edges { 
            node { 
                id
                name
                tpaadjusterSet {
                    edges {
                        node {
                            id
                            name
                        }
                    }
                }
            } 
        }
    }
  }
`
const ALL_INCIDENT_PARTIES = gql`
  query AllIncidentParties($incidentId: ID!) {
    allIncidentParties(incidentId: $incidentId) {
      edges {
        node {
          id
          partyType
          partyName
        }
      }
    }
  }
`

const AddSubrogationClaimModal = ({ isOpen, toggle, incidentId, onSuccess }) => {
    const [error, setError] = useState(null)
    const [openAddAdjusterModal, setOpenAddAdjusterModal] = useState(false)
    const [adjusterOptions, setAdjusterOptions] = useState([])

    const { data: statusChoicesData } = useQuery(SUBROGATION_STATUS_CHOICES)
    const { data: tpaData, refetch: refetchTpas } = useQuery(ALL_TPAS, { variables: { incidentId }, skip: !incidentId })
    const { data: partiesData } = useQuery(ALL_INCIDENT_PARTIES, { variables: { incidentId }, skip: !incidentId })
    const [createClaim, { loading }] = useMutation(CREATE_SUBROGATION_CLAIM)

    const statusOptions = statusChoicesData?.subrogationStatusChoices?.enumValues?.map(opt => ({
        label: opt.description || opt.name,
        value: opt.name
    })) || []

    const tpaOptions = useMemo(() => {
        return tpaData?.allTpas?.edges?.map(edge => ({
            label: edge.node.name,
            value: edge.node.id,
            adjusters: edge.node.tpaadjusterSet.edges.map(adj => ({
                label: adj.node.name,
                value: adj.node.id
            }))
        })) || []
    }, [tpaData])

    const parties = partiesData?.allIncidentParties?.edges?.map(edge => edge.node) || []

    const { control, handleSubmit, formState: { errors }, reset, watch } = useForm({
        resolver: yupResolver(SubrogationClaimSchema),
        defaultValues: { incidentId },
        mode: "onSubmit",
    })

    const selectedTpaId = watch("tpaId")

    useEffect(() => {
        const selectedTpaData = tpaOptions.find(opt => opt.value === selectedTpaId)
        setAdjusterOptions(selectedTpaData?.adjusters || [])
    }, [selectedTpaId, tpaOptions])

    const toggleAdjusterModal = () => setOpenAddAdjusterModal(!openAddAdjusterModal)

    const closeAndReset = () => {
        reset()
        toggle()
    }

    const handleAdjusterCreated = async (adjuster) => {
        setOpenAddAdjusterModal(false)
        if (!adjuster?.id) return
        const createdAdjuster = {
            label: adjuster.name,
            value: adjuster.id
        }
        await refetchTpas()
        setAdjusterOptions(prev => {
            const filtered = prev.filter(a => a.value !== createdAdjuster.value)
            return [...filtered, createdAdjuster]
        })
        reset({ ...watch(), adjusterId: createdAdjuster.value })
    }

    const onSubmit = async (values) => {
        const input = { ...values }
        try {
            const { data } = await createClaim({ variables: { input } })
            if (data?.createSubrogationClaim?.ok) {
                onSuccess && onSuccess()
                closeAndReset()
            }
            if (data?.createSubrogationClaim?.errors?.length) {
                setError(data.createSubrogationClaim.errors[0].messages[0])
            }
        } catch (e) {
            setError(e.message)
        }
    }
    const booleanOptions = [{ value: true, label: "Yes" }, { value: false, label: "No" }]

    return (
        <Modal isOpen={isOpen} toggle={closeAndReset} className='subrogation-claim-modal'>
            <ModalHeader>Add Subrogation Claim</ModalHeader>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <ModalBody>
                    <Controller name="incidentId" control={control} render={({ field }) => <Input type="hidden" {...field} />} />
                    <Row>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="partyId">Party <span style={{ color: 'red' }}>*</span></Label>
                                <Controller
                                    name="partyId"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            className="bos-custom-select"
                                            classNamePrefix="bos-select"
                                            inputId="partyId"
                                            options={parties.map(p => ({ value: p.id, label: p.partyName }))}
                                            value={parties.map(p => ({ value: p.id, label: p.partyName })).find(opt => opt.value === field.value) || null}
                                            onChange={opt => field.onChange(opt ? opt.value : '')}
                                            isClearable
                                            placeholder="Select the party involved"
                                        />
                                    )}
                                />
                                <FormFeedback style={{ display: errors.partyId ? 'block' : 'none' }}>{errors.partyId?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="tpaId">TPA (Third Party Administrator) <span style={{ color: 'red' }}>*</span></Label>
                                <Controller
                                    name="tpaId"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            className="bos-custom-select"
                                            classNamePrefix="bos-select"
                                            inputId="tpaId"
                                            options={tpaOptions}
                                            value={tpaOptions.find(opt => opt.value === field.value) || null}
                                            onChange={opt => field.onChange(opt ? opt.value : '')}
                                            isClearable
                                            placeholder="Select TPA"
                                        />
                                    )}
                                />
                                <FormFeedback style={{ display: errors.tpaId ? 'block' : 'none' }}>{errors.tpaId?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="status">Status</Label>
                                <Controller
                                    name="status"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            className="bos-custom-select"
                                            classNamePrefix="bos-select"
                                            inputId="status"
                                            options={statusOptions}
                                            value={statusOptions.find(opt => opt.value === field.value) || null}
                                            onChange={opt => field.onChange(opt ? opt.value : '')}
                                            isClearable
                                            placeholder="Select claim status"
                                        />
                                    )}
                                />
                                <FormFeedback style={{ display: errors.status ? 'block' : 'none' }}>{errors.status?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="externalId">External ID</Label>
                                <Controller name="externalId" control={control} render={({ field }) => (
                                    <Input id="externalId" {...field} invalid={!!errors.externalId} placeholder="Enter external claim ID (if any)" />
                                )} />
                                <FormFeedback>{errors.externalId?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <Row>
                                <Col xs={selectedTpaId ? 10 : 12}>
                                    <FormGroup>
                                        <Label for="adjusterId">TPA Adjuster</Label>
                                        <Controller
                                            name="adjusterId"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    className="bos-custom-select"
                                                    classNamePrefix="bos-select"
                                                    inputId="adjusterId"
                                                    isDisabled={!selectedTpaId}
                                                    options={adjusterOptions}
                                                    value={adjusterOptions.find(opt => opt.value === field.value) || null}
                                                    onChange={opt => field.onChange(opt ? opt.value : '')}
                                                    isClearable
                                                    placeholder="Select TPA Adjuster"
                                                />
                                            )}
                                        />
                                        <FormFeedback style={{ display: errors.adjusterId ? 'block' : 'none' }}>{errors.adjusterId?.message}</FormFeedback>
                                    </FormGroup>
                                </Col>
                                {selectedTpaId && (
                                    <Col xs={2} className='pl-0'>
                                        <Label>&nbsp;</Label>
                                        <Button color="primary" id="add-adjuster-button" onClick={toggleAdjusterModal}>
                                            <i className='fa fa-plus' />
                                        </Button>
                                        <UncontrolledTooltip target="add-adjuster-button" placement="top">
                                            Add Adjuster
                                        </UncontrolledTooltip>
                                    </Col>
                                )}
                            </Row>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="filedDate">Filed Date</Label>
                                <Controller name="filedDate" control={control} render={({ field }) => (
                                    <Input id="filedDate" type="date" {...field} invalid={!!errors.filedDate} placeholder="Select filed date" />
                                )} />
                                <FormFeedback>{errors.filedDate?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="acknowledgmentDate">Acknowledgment Date</Label>
                                <Controller name="acknowledgmentDate" control={control} render={({ field }) => (
                                    <Input id="acknowledgmentDate" type="date" {...field} invalid={!!errors.acknowledgmentDate} placeholder="Select acknowledgment date" />
                                )} />
                                <FormFeedback>{errors.acknowledgmentDate?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="paymentId">Payment ID</Label>
                                <Controller name="paymentId" control={control} render={({ field }) => (
                                    <Input id="paymentId" {...field} invalid={!!errors.paymentId} placeholder="Enter payment reference ID (if any)" />
                                )} />
                                <FormFeedback>{errors.paymentId?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="inLitigation">In Litigation</Label>
                                <Controller
                                    name="inLitigation"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            className="bos-custom-select"
                                            classNamePrefix="bos-select"
                                            inputId="inLitigation"
                                            options={booleanOptions}
                                            value={booleanOptions.find(opt => opt.value === field.value) || null}
                                            onChange={opt => field.onChange(opt ? opt.value : null)}
                                            isClearable
                                            placeholder="Select litigation status"
                                        />
                                    )}
                                />
                                <FormFeedback style={{ display: errors.inLitigation ? 'block' : 'none' }}>{errors.inLitigation?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="tpaExpenses">TPA Expenses</Label>
                                <Controller name="tpaExpenses" control={control} render={({ field }) => (
                                    <Input id="tpaExpenses" type="number" {...field} invalid={!!errors.tpaExpenses} placeholder="Enter TPA expenses" />
                                )} />
                                <FormFeedback>{errors.tpaExpenses?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="estimatedRepair">Estimated Repair</Label>
                                <Controller name="estimatedRepair" control={control} render={({ field }) => (
                                    <Input id="estimatedRepair" type="number" {...field} invalid={!!errors.estimatedRepair} placeholder="Enter estimated repair cost" />
                                )} />
                                <FormFeedback>{errors.estimatedRepair?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="estimatedPd">Estimated Property Damage</Label>
                                <Controller name="estimatedPd" control={control} render={({ field }) => (
                                    <Input id="estimatedPd" type="number" {...field} invalid={!!errors.estimatedPd} placeholder="Enter estimated property damage amount" />
                                )} />
                                <FormFeedback>{errors.estimatedPd?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="estimatedLou">Estimated Loss of Use</Label>
                                <Controller name="estimatedLou" control={control} render={({ field }) => (
                                    <Input id="estimatedLou" type="number" {...field} invalid={!!errors.estimatedLou} placeholder="Enter estimated loss of use amount" />
                                )} />
                                <FormFeedback>{errors.estimatedLou?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="estimatedDv">Estimated Damage Vehicle</Label>
                                <Controller name="estimatedDv" control={control} render={({ field }) => (
                                    <Input id="estimatedDv" type="number" {...field} invalid={!!errors.estimatedDv} placeholder="Enter estimated diminished value" />
                                )} />
                                <FormFeedback>{errors.estimatedDv?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="estimatedTs">Estimated Tow & Storage</Label>
                                <Controller name="estimatedTs" control={control} render={({ field }) => (
                                    <Input id="estimatedTs" type="number" {...field} invalid={!!errors.estimatedTs} placeholder="Enter estimated tow/storage amount" />
                                )} />
                                <FormFeedback>{errors.estimatedTs?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="recoveredPd">Recovered Property Damage</Label>
                                <Controller name="recoveredPd" control={control} render={({ field }) => (
                                    <Input id="recoveredPd" type="number" {...field} invalid={!!errors.recoveredPd} placeholder="Enter recovered property damage amount" />
                                )} />
                                <FormFeedback>{errors.recoveredPd?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="recoveredLou">Recovered Loss of Use</Label>
                                <Controller name="recoveredLou" control={control} render={({ field }) => (
                                    <Input id="recoveredLou" type="number" {...field} invalid={!!errors.recoveredLou} placeholder="Enter recovered loss of use amount" />
                                )} />
                                <FormFeedback>{errors.recoveredLou?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="recoveredDv">Recovered Damage Vehicle</Label>
                                <Controller name="recoveredDv" control={control} render={({ field }) => (
                                    <Input id="recoveredDv" type="number" {...field} invalid={!!errors.recoveredDv} placeholder="Enter recovered diminished value" />
                                )} />
                                <FormFeedback>{errors.recoveredDv?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="recoveredTs">Recovered Tow & Storage</Label>
                                <Controller name="recoveredTs" control={control} render={({ field }) => (
                                    <Input id="recoveredTs" type="number" {...field} invalid={!!errors.recoveredTs} placeholder="Enter recovered tow/storage amount" />
                                )} />
                                <FormFeedback>{errors.recoveredTs?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="dailyRate">Daily Rate</Label>
                                <Controller name="dailyRate" control={control} render={({ field }) => (
                                    <Input id="dailyRate" type="number" {...field} invalid={!!errors.dailyRate} placeholder="Enter daily rate (if applicable)" />
                                )} />
                                <FormFeedback>{errors.dailyRate?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="daysDown">Days Down</Label>
                                <Controller name="daysDown" control={control} render={({ field }) => (
                                    <Input id="daysDown" type="number" {...field} invalid={!!errors.daysDown} placeholder="Enter number of days down" />
                                )} />
                                <FormFeedback>{errors.daysDown?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={12}>
                            <FormGroup>
                                <Label for="notes">Notes</Label>
                                <Controller name="notes" control={control} render={({ field }) => (
                                    <Input id="notes" type="textarea" {...field} invalid={!!errors.notes} placeholder="Enter any additional notes or description" />
                                )} />
                                <FormFeedback>{errors.notes?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>
                    {error && <div className="text-danger small mb-2">{error}</div>}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={closeAndReset}>Cancel</Button>
                    <Button color="primary" type="submit" disabled={loading}>Add</Button>
                </ModalFooter>
            </Form>
            {openAddAdjusterModal && (
                <AddAdjusterModal
                    isOpen={openAddAdjusterModal}
                    toggle={() => setOpenAddAdjusterModal(false)}
                    tpaId={selectedTpaId}
                    onCreate={handleAdjusterCreated}
                />
            )}
        </Modal>
    )
}

export default AddSubrogationClaimModal
